import { Sheet, PouchColorLegend, Pouch, Group } from '../types';
import { isInMuteGroup, pouchCountsAsTimeForSheetDisplay } from './GroupLogic';
import { getPouchInclusiveDivisionDistributedValue } from './InclusiveDivGtpLogic';
import { getPouchContributionToSheetCount } from './StatisticsLogic';
import React from 'react';

/**
 * 凡例用の1パウチ分寄与。通常はシートと同じ `getPouchContributionForTotals`。
 * ÷内包GTPがあるグループだけ例外: マージ後のグループ実効（シート1行）を、
 * 葉の実効寄与（value×|mult| 等）に比例して色へ分配。内包GTP行（×・÷とも）は凡例0。
 * 親が複数とも÷内包を持つときは二重按分を避け従来計上にフォールバック。
 * （計算本体は InclusiveDivGtpLogic.getPouchInclusiveDivisionDistributedValue）
 */
export const getPouchContributionForLegend = (
  pouch: Pouch,
  groups: Group[],
  allPouches: Pouch[]
): number =>
  getPouchInclusiveDivisionDistributedValue(pouch, groups, allPouches);

// 色の合計（凡例表示専用。÷内包時はマージ実効を葉へ比例配分し GTP 行は0）
export const calculateColorTotal = (pouches: Pouch[], colorId: string, groups: Group[] = []): number => {
  const total = pouches
    .filter(
      (p) =>
        p.color === colorId &&
        !p._isDeleting &&
        !p._isGtpRenderPreview &&
        !isInMuteGroup(p.id, groups)
    )
    .reduce((sum, p) => sum + getPouchContributionForLegend(p, groups, pouches), 0);
  return Math.round(total * 1000000000) / 1000000000;
};

/** 凡例色の所属パウチがすべて時間表示か（レイヤー mirror 後の pouches を渡す） */
export function colorLegendShowsTimeConversion(
  colorId: string,
  pouches: Pouch[],
  groups: Group[] = [],
  balancingPouchId?: string | null,
  sheet?: Sheet,
): boolean {
  const members = pouches.filter(
    p =>
      p.color === colorId &&
      !p._isDeleting &&
      !p._isGtpRenderPreview &&
      !isInMuteGroup(p.id, groups)
  );
  if (members.length === 0) return false;
  return members.every(p =>
    pouchCountsAsTimeForSheetDisplay(p, pouches, groups, balancingPouchId)
  );
};

// 色のカウントを計算
export const calculateColorCount = (pouches: Pouch[], colorId: string, groups: Group[] = []): number => {
  const count = pouches
    .filter(
      (p) =>
        p.color === colorId &&
        !p._isDeleting &&
        !p._isGtpRenderPreview &&
        !isInMuteGroup(p.id, groups)
    )
    .reduce(
      (count, p) => count + getPouchContributionToSheetCount(p, null, groups, pouches),
      0
    );
  return Math.round(count * 1000000000) / 1000000000;
};

// 凡例を更新
export const updateColorLegend = async (
  sheet: Partial<Sheet> & { id: string; colorLegends?: PouchColorLegend[] },
  colorId: string,
  legend: string,
  onUpdateActiveSheet: (
    pouches: Pouch[] | null,
    groups: Group[] | null,
    isCritical?: boolean,
    saveToDb?: boolean,
    sheetParams?: Partial<Sheet>
  ) => void,
  dispatch?: React.Dispatch<any>
): Promise<void> => {
  try {
    const updatedLegends =
      sheet.colorLegends?.map((l) => (l.colorId === colorId ? { ...l, legend } : l)) || [];

    onUpdateActiveSheet(null, null, true, true, { colorLegends: updatedLegends });
  } catch (error) {
    console.error('Failed to update color legend:', error);
  }
};
